import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * COMMENTS API
 *
 * API client for comment operations on test cases and test steps.
 *
 * Endpoints:
 * - GET    /comments/testcases/{testCaseId}           - List test case comments
 * - POST   /comments/testcases/{testCaseId}           - Create test case comment
 * - GET    /comments/teststeps/{testStepId}           - List test step comments (future)
 * - POST   /comments/teststeps/{testStepId}           - Create test step comment (future)
 * - GET    /comments/{commentId}                       - Get single comment
 * - PUT    /comments/{commentId}                       - Update comment
 * - DELETE /comments/{commentId}                       - Delete comment
 */

// Create axios instance for comments API
const commentsClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all requests.
 */
commentsClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token for comments API:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * RESPONSE INTERCEPTOR
 * Handles authentication errors.
 */
commentsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Comments API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// TEST CASE COMMENTS
// ============================================================================

/**
 * GET TEST CASE COMMENTS
 *
 * Retrieves all comments for a specific test case.
 * Comments are returned in chronological order (oldest first).
 *
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of comment objects
 */
export const getTestCaseComments = async (testCaseId) => {
  try {
    const response = await commentsClient.get(`/comments/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test case comments:', error)
    throw error
  }
}

/**
 * CREATE TEST CASE COMMENT
 *
 * Creates a new comment on a test case.
 *
 * @param {number|string} testCaseId - The test case ID
 * @param {string} content - Comment text (max 10,000 characters)
 * @returns {Promise<Object>} Created comment object
 */
export const createTestCaseComment = async (testCaseId, content) => {
  try {
    const response = await commentsClient.post(
      `/comments/testcases/${testCaseId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error creating test case comment:', error)
    throw error
  }
}

// ============================================================================
// TEST STEP COMMENTS (for future use)
// ============================================================================

/**
 * GET TEST STEP COMMENTS
 *
 * Retrieves all comments for a specific test step.
 * Note: The API documentation doesn't include test step comments,
 * but this is included for future extensibility.
 *
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of comment objects
 */
export const getTestStepComments = async (testStepId) => {
  try {
    const response = await commentsClient.get(`/comments/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test step comments:', error)
    throw error
  }
}

/**
 * CREATE TEST STEP COMMENT
 *
 * Creates a new comment on a test step.
 *
 * @param {number|string} testStepId - The test step ID
 * @param {string} content - Comment text (max 10,000 characters)
 * @returns {Promise<Object>} Created comment object
 */
export const createTestStepComment = async (testStepId, content) => {
  try {
    const response = await commentsClient.post(
      `/comments/teststeps/${testStepId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error creating test step comment:', error)
    throw error
  }
}

// ============================================================================
// INDIVIDUAL COMMENT OPERATIONS
// ============================================================================

/**
 * GET COMMENT BY ID
 *
 * Retrieves a single comment by its ID.
 *
 * @param {number|string} commentId - The comment ID
 * @returns {Promise<Object>} Comment object
 */
export const getComment = async (commentId) => {
  try {
    const response = await commentsClient.get(`/comments/${commentId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching comment:', error)
    throw error
  }
}

/**
 * UPDATE COMMENT
 *
 * Updates an existing comment's content.
 * Only the original author can update their own comments.
 *
 * @param {number|string} commentId - The comment ID
 * @param {string} content - Updated comment text (max 10,000 characters)
 * @returns {Promise<Object>} Updated comment object
 */
export const updateComment = async (commentId, content) => {
  try {
    const response = await commentsClient.put(
      `/comments/${commentId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error updating comment:', error)
    throw error
  }
}

/**
 * DELETE COMMENT
 *
 * Deletes a comment by its ID.
 * Only the original author can delete their own comments.
 *
 * @param {number|string} commentId - The comment ID
 * @returns {Promise<void>}
 */
export const deleteComment = async (commentId) => {
  try {
    await commentsClient.delete(`/comments/${commentId}`)
  } catch (error) {
    console.error('Error deleting comment:', error)
    throw error
  }
}

// ============================================================================
// EXECUTION COMMENTS (for execution details modal)
// ============================================================================

/**
 * GET TEST CASE EXECUTION COMMENTS
 *
 * Retrieves all comments for a specific test case execution.
 * Uses runId + testCaseId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of comment objects
 */
export const getTestCaseExecutionComments = async (runId, testCaseId) => {
  try {
    const response = await commentsClient.get(`/comments/runs/${runId}/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test case execution comments:', error)
    throw error
  }
}

/**
 * CREATE TEST CASE EXECUTION COMMENT
 *
 * Creates a new comment on a test case execution.
 * Uses runId + testCaseId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @param {string} content - Comment text (max 10,000 characters)
 * @returns {Promise<Object>} Created comment object
 */
export const createTestCaseExecutionComment = async (runId, testCaseId, content) => {
  try {
    const response = await commentsClient.post(
      `/comments/runs/${runId}/testcases/${testCaseId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error creating test case execution comment:', error)
    throw error
  }
}

/**
 * GET TEST STEP EXECUTION COMMENTS
 *
 * Retrieves all comments for a specific test step execution.
 * Uses runId + testStepId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of comment objects
 */
export const getTestStepExecutionComments = async (runId, testStepId) => {
  try {
    const response = await commentsClient.get(`/comments/runs/${runId}/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test step execution comments:', error)
    throw error
  }
}

/**
 * CREATE TEST STEP EXECUTION COMMENT
 *
 * Creates a new comment on a test step execution.
 * Uses runId + testStepId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @param {string} content - Comment text (max 10,000 characters)
 * @returns {Promise<Object>} Created comment object
 */
export const createTestStepExecutionComment = async (runId, testStepId, content) => {
  try {
    const response = await commentsClient.post(
      `/comments/runs/${runId}/teststeps/${testStepId}`,
      { content }
    )
    return response.data
  } catch (error) {
    console.error('Error creating test step execution comment:', error)
    throw error
  }
}
